'use client';

import { CircleCheck, CircleX, FileIcon, LoaderCircle, X } from 'lucide-react';
import type { FileUploadStatus } from '../hooks/useManagedFileUpload';
import { formatBytes } from '../lib/bytes';
import { cn } from '../lib/utils';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

export interface FileUploadQueueProps {
  statuses: Record<string, FileUploadStatus>;
  onCancel: (key: string) => void;
  className?: string;
}

const ACTIVE_STAGES = new Set(['requesting', 'uploading']);
export function FileUploadQueue({ statuses, onCancel, className }: FileUploadQueueProps) {
  const rows = Object.values(statuses);
  if (rows.length === 0) return null;

  return (
    <section className={cn('space-y-2', className)} aria-label="Upload progress">
      {rows.map((upload) => {
        const isActive = ACTIVE_STAGES.has(upload.status);
        const statusLabel =
          upload.status === 'success'
            ? 'Uploaded'
            : // entity-renderer-ignore: upload.status is a fixed transfer lifecycle, not a workspace task status.
              upload.status === 'cancelled'
              ? 'Cancelled'
              : upload.status === 'error'
                ? 'Failed'
                : upload.status === 'registering'
                  ? 'Saving'
                  : upload.status === 'requesting'
                    ? 'Preparing'
                    : 'Uploading';

        return (
          <div
            key={upload.key}
            className="rounded-lg border border-border bg-background p-3 shadow-sm"
          >
            <div className="flex min-w-0 items-center gap-3">
              <FileIcon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p
                  className="truncate text-sm font-medium text-foreground"
                  title={upload.file.name}
                >
                  {upload.file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatBytes(upload.uploadedBytes)} of {formatBytes(upload.totalBytes)} ·{' '}
                  {upload.progress}%
                </p>
              </div>
              {isActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  onClick={() => onCancel(upload.key)}
                  aria-label={`Cancel upload of ${upload.file.name}`}
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              )}
              {upload.status === 'success' && (
                <CircleCheck className="size-5 shrink-0 text-emerald-600" aria-label="Uploaded" />
              )}
              {upload.status === 'error' && (
                <CircleX className="size-5 shrink-0 text-destructive" aria-label="Upload failed" />
              )}
              {['requesting', 'uploading', 'registering'].includes(upload.status) && (
                <LoaderCircle
                  className="size-5 shrink-0 animate-spin text-primary"
                  aria-label={statusLabel}
                />
              )}
            </div>
            <Progress value={upload.progress} className="mt-3 h-1.5" />
            <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
              {upload.error ?? statusLabel}
            </p>
          </div>
        );
      })}
    </section>
  );
}
